import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  NotificationsClientService,
  type UserNotificationDeliveryResponse,
  type UserNotificationPreferencesResponse,
  type UserNotificationResponse,
} from '@automatick/shared-ng';
import { BehaviorSubject, finalize, Subject, take, takeUntil } from 'rxjs';
import { AppStateService } from './app-state.service';
import { UserNotificationCommandRegistry } from './user-notification-command-registry';
import {
  type UserNotificationPreferencePatch,
  UserNotificationContractValidator,
} from './user-notification-contract-validator';
import {
  NotificationLifecyclePhase,
  type NotificationLifecyclePhase as NotificationLifecyclePhaseType,
} from './user-notification.models';

const NOTIFICATION_DELIVERY_HISTORY_TAKE = 50;

export interface UserNotificationSecondaryState {
  readonly workspaceId: string | null;
  readonly deliveryHistory: readonly UserNotificationDeliveryResponse[];
  readonly selectedDeliveryNotificationId: string;
  readonly preferences: UserNotificationPreferencesResponse | null;
  readonly isDeliveryHistoryLoading: boolean;
  readonly isPreferencesSaving: boolean;
  readonly deliveryPhase: NotificationLifecyclePhaseType;
  readonly preferencesPhase: NotificationLifecyclePhaseType;
  readonly deliveryMessage: string;
  readonly preferencesMessage: string;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationSecondaryRuntime implements OnDestroy {
  private readonly appState = inject(AppStateService);
  private readonly notificationsClient = inject(NotificationsClientService);
  private readonly registry = inject(UserNotificationCommandRegistry);
  private readonly contracts = new UserNotificationContractValidator();
  private readonly destroy$ = new Subject<void>();
  private readonly stateSubject =
    new BehaviorSubject<UserNotificationSecondaryState>({
      workspaceId: null,
      deliveryHistory: [],
      selectedDeliveryNotificationId: '',
      preferences: null,
      isDeliveryHistoryLoading: false,
      isPreferencesSaving: false,
      deliveryPhase: NotificationLifecyclePhase.Idle,
      preferencesPhase: NotificationLifecyclePhase.Idle,
      deliveryMessage: '',
      preferencesMessage: '',
    });
  private destroyed = false;
  private deliveryGeneration = 0;
  private preferenceUiGeneration = 0;

  readonly state$ = this.stateSubject.asObservable();

  constructor() {
    this.registry.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const workspaceId = this.stateSubject.value.workspaceId;
      this.patch({
        isPreferencesSaving: workspaceId
          ? this.registry.hasPreferenceLease(workspaceId)
          : false,
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.deliveryGeneration += 1;
    this.preferenceUiGeneration += 1;
    this.destroy$.next();
    this.destroy$.complete();
  }

  activateWorkspace(workspaceId: string | null): void {
    if (this.stateSubject.value.workspaceId === workspaceId) {
      return;
    }
    this.deliveryGeneration += 1;
    this.preferenceUiGeneration += 1;
    this.stateSubject.next({
      workspaceId,
      deliveryHistory: [],
      selectedDeliveryNotificationId: '',
      preferences: null,
      isDeliveryHistoryLoading: false,
      isPreferencesSaving: workspaceId
        ? this.registry.hasPreferenceLease(workspaceId)
        : false,
      deliveryPhase: NotificationLifecyclePhase.Idle,
      preferencesPhase: NotificationLifecyclePhase.Idle,
      deliveryMessage: '',
      preferencesMessage: '',
    });
    if (workspaceId) {
      this.loadPreferences();
    }
  }

  loadPreferences(): void {
    const workspaceId = this.stateSubject.value.workspaceId;
    if (!workspaceId || this.destroyed) {
      return;
    }
    if (this.registry.hasPreferenceLease(workspaceId)) {
      this.patch({ isPreferencesSaving: true });
      return;
    }
    const generation = this.preferenceUiGeneration + 1;
    this.preferenceUiGeneration = generation;
    this.patch({
      isPreferencesSaving: this.registry.hasPreferenceLease(workspaceId),
      preferencesPhase: NotificationLifecyclePhase.Loading,
      preferencesMessage: '',
    });
    this.notificationsClient
      .getUserNotificationPreferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preferences) => {
          if (!this.isPreferenceUiCurrent(generation, workspaceId)) {
            return;
          }
          const ownedPreferences = this.contracts.normalizeOwnedPreferences(
            preferences,
            workspaceId,
            this.currentUserId(),
          );
          this.patch(
            ownedPreferences
              ? {
                  preferences: ownedPreferences,
                  preferencesPhase: NotificationLifecyclePhase.Ready,
                }
              : {
                  preferencesPhase: NotificationLifecyclePhase.Error,
                  preferencesMessage:
                    'Notification preferences could not be verified.',
                },
          );
        },
        error: () => {
          if (this.isPreferenceUiCurrent(generation, workspaceId)) {
            this.patch({
              preferencesPhase: NotificationLifecyclePhase.Error,
              preferencesMessage:
                'Notification preferences are temporarily unavailable.',
            });
          }
        },
      });
  }

  updatePreferences(patch: UserNotificationPreferencePatch): boolean {
    const workspaceId = this.stateSubject.value.workspaceId;
    if (!workspaceId || this.destroyed) {
      return false;
    }
    const lease = this.registry.acquirePreference(workspaceId);
    if (!lease) {
      return false;
    }
    const generation = this.preferenceUiGeneration + 1;
    this.preferenceUiGeneration = generation;
    this.patch({
      isPreferencesSaving: true,
      preferencesPhase: NotificationLifecyclePhase.Loading,
      preferencesMessage: '',
    });

    this.notificationsClient
      .updateUserNotificationPreferences({
        body: {
          realtimeEnabled: patch.realtimeEnabled,
          verboseEnabled: patch.verboseEnabled,
          webEnabled: patch.webEnabled,
          mobileEnabled: patch.mobileEnabled,
          watchEnabled: patch.watchEnabled,
          glassesEnabled: patch.glassesEnabled,
          emailEnabled: patch.emailEnabled,
          smsEnabled: patch.smsEnabled,
        },
      })
      .pipe(
        take(1),
        finalize(() => {
          this.registry.releasePreference(lease);
          if (this.isPreferenceUiCurrent(generation, workspaceId)) {
            this.patch({
              isPreferencesSaving:
                this.registry.hasPreferenceLease(workspaceId),
            });
          }
        }),
      )
      .subscribe({
        next: (preferences) => {
          if (!this.isPreferenceUiCurrent(generation, workspaceId)) {
            return;
          }
          const ownedPreferences = this.contracts.normalizeOwnedPreferences(
            preferences,
            workspaceId,
            this.currentUserId(),
          );
          this.patch(
            ownedPreferences
              ? {
                  preferences: ownedPreferences,
                  preferencesPhase: NotificationLifecyclePhase.Ready,
                }
              : {
                  preferencesPhase: NotificationLifecyclePhase.Error,
                  preferencesMessage:
                    'Notification preferences could not be verified.',
                },
          );
        },
        error: () => {
          if (this.isPreferenceUiCurrent(generation, workspaceId)) {
            this.patch({
              preferencesPhase: NotificationLifecyclePhase.Error,
              preferencesMessage:
                'Notification preferences could not be saved.',
            });
          }
        },
      });
    return true;
  }

  loadDeliveryHistory(notification: UserNotificationResponse): void {
    const workspaceId = this.stateSubject.value.workspaceId;
    const notificationId = notification.notificationId?.trim();
    if (
      !workspaceId ||
      !notificationId ||
      notification.workspaceId !== workspaceId ||
      this.destroyed
    ) {
      return;
    }
    const generation = this.deliveryGeneration + 1;
    this.deliveryGeneration = generation;
    this.patch({
      selectedDeliveryNotificationId: notificationId,
      deliveryHistory: [],
      isDeliveryHistoryLoading: true,
      deliveryPhase: NotificationLifecyclePhase.Loading,
      deliveryMessage: '',
    });

    this.notificationsClient
      .queryUserNotificationDeliveries({
        body: { notificationId, take: NOTIFICATION_DELIVERY_HISTORY_TAKE },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deliveries) => {
          if (
            !this.isDeliveryCurrent(generation, workspaceId, notificationId)
          ) {
            return;
          }
          const validDeliveries = this.contracts.selectOwnedDeliveries(
            deliveries,
            workspaceId,
            notificationId,
          );
          this.patch({
            deliveryHistory: validDeliveries,
            isDeliveryHistoryLoading: false,
            deliveryPhase:
              validDeliveries.length !== deliveries.length
                ? NotificationLifecyclePhase.Partial
                : validDeliveries.length > 0
                  ? NotificationLifecyclePhase.Ready
                  : NotificationLifecyclePhase.Empty,
          });
        },
        error: () => {
          if (this.isDeliveryCurrent(generation, workspaceId, notificationId)) {
            this.patch({
              isDeliveryHistoryLoading: false,
              deliveryPhase: NotificationLifecyclePhase.Error,
              deliveryMessage:
                'Notification delivery history could not be loaded.',
            });
          }
        },
      });
  }

  closeDeliveryHistory(): void {
    this.deliveryGeneration += 1;
    this.patch({
      selectedDeliveryNotificationId: '',
      deliveryHistory: [],
      isDeliveryHistoryLoading: false,
      deliveryPhase: NotificationLifecyclePhase.Idle,
      deliveryMessage: '',
    });
  }

  closeDeliveryIfMissing(
    notifications: readonly UserNotificationResponse[],
  ): void {
    const selectedId = this.stateSubject.value.selectedDeliveryNotificationId;
    if (
      selectedId &&
      !notifications.some(
        (notification) => notification.notificationId === selectedId,
      )
    ) {
      this.closeDeliveryHistory();
    }
  }

  private isPreferenceUiCurrent(
    generation: number,
    workspaceId: string,
  ): boolean {
    return (
      !this.destroyed &&
      this.preferenceUiGeneration === generation &&
      this.stateSubject.value.workspaceId === workspaceId
    );
  }

  private isDeliveryCurrent(
    generation: number,
    workspaceId: string,
    notificationId: string,
  ): boolean {
    return (
      !this.destroyed &&
      this.deliveryGeneration === generation &&
      this.stateSubject.value.workspaceId === workspaceId &&
      this.stateSubject.value.selectedDeliveryNotificationId === notificationId
    );
  }

  private currentUserId(): string | null {
    return this.appState.getStateSnapshot().user?.userId?.trim() || null;
  }

  private patch(patch: Partial<UserNotificationSecondaryState>): void {
    if (!this.destroyed) {
      this.stateSubject.next({ ...this.stateSubject.value, ...patch });
    }
  }
}
